import * as React from "react";
import { useState } from "react";
import styled from "styled-components";
import Projects from "./Projects";

export interface CompanyData<T extends ProjectData> {
  name: string;
  id: number;
  projects: T[];
}

export enum ProjectStatus {
  notActive = 0,
  ongoing = 10,
  readyForInvoice = 20,
  invoiceComplete = 30,
}

export interface ProjectData {
  name: string;
  id: number;
  status: ProjectStatus;
  orderDate: string;
}

export interface ProjectFormData {
  name: string;
  orderDate: string;
  id: number;
  status: ProjectStatus;
  newStatus: ProjectStatus;
}

interface Urls {
  back: string;
  saveProjects: string;
  showAllProjects: string;
  showActiveProjects: string;
}

interface PropsData {
  urls: Urls;
  companies: CompanyData<ProjectData>[];
  showAllProjects: boolean;
}

export interface SaveProjectsResponse {
  status: number;
  message?: string;
  successful?: number[];
  failed?: {
    id: number;
    reason: SaveFailReason;
    newStatus: ProjectStatus | null;
  }[];
}

export enum SaveFailReason {
  StatusChanged,
  NotFound,
}

interface Props {
  data: PropsData;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  & > * {
    margin: 0;
  }

  & > .companies {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
    max-width: 1000px;
  }

  & > .buttons {
    display: flex;
    flex-direction: row;
    gap: 0.25rem;
  }
`;

const FinalizeProjectsPage = ({ data }: Props) => {
  const [formData, setFormData] = useState(
    [] as CompanyData<ProjectFormData>[]
  );

  React.useEffect(() => {
    const newFormData = data.companies.map(
      (company) =>
        ({
          id: company.id,
          name: company.name,
          projects: company.projects.map(
            (project) =>
              ({
                id: project.id,
                name: project.name,
                orderDate: project.orderDate,
                status: project.status,
                newStatus: project.status,
              } as ProjectFormData)
          ),
        } as CompanyData<ProjectFormData>)
    );

    setFormData(newFormData);
  }, [data.companies]);

  const onProjectStatusChanged = (
    companyId: number,
    projectId: number,
    newStatus: ProjectStatus
  ) => {
    const newFormData = formData.map((company) => {
      if (company.id !== companyId) {
        return company;
      }

      return {
        id: company.id,
        name: company.name,
        projects: company.projects.map((project) => {
          if (project.id !== projectId) {
            return project;
          }

          return {
            id: project.id,
            name: project.name,
            orderDate: project.orderDate,
            status: project.status,
            newStatus: newStatus,
          };
        }),
      };
    });

    setFormData(newFormData);
  };

  const onSaveProjects = async (
    company: CompanyData<ProjectFormData>,
    projects: ProjectFormData[]
  ): Promise<SaveProjectsResponse | null> => {
    let postData = {
      projects: projects.map((p) => ({
        id: p.id,
        currentStatus: p.status,
        newStatus: p.newStatus,
      })),
    };

    const response = await fetch(data.urls.saveProjects, {
      method: "POST",
      body: JSON.stringify(postData),
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const text = await response.text();
    let json: SaveProjectsResponse;

    try {
      json = JSON.parse(text);
    } catch (err) {
      // Most likely an uncaught exception was thrown on the serverside, causing the default error page to show.
      json = { message: "Ett serverfel inträffade", status: 500 };
    }

    if (json.status == 200 || json.status == 409) {
      setFormData(
        formData.map((company2) => {
          if (company2.id !== company.id) {
            return company2;
          }

          const newProjects = [] as ProjectFormData[];

          for (let i = 0; i < company2.projects.length; ++i) {
            const project = company2.projects[i];

            if (json.successful?.indexOf(project.id) !== -1) {
              if (
                !data.showAllProjects &&
                (project.newStatus == ProjectStatus.invoiceComplete ||
                  project.newStatus === ProjectStatus.notActive)
              ) {
                // ignore, don't add it to new projects since we're only showing non completed and active projects
              } else {
                // set status to newStatus
                newProjects.push({
                  ...project,
                  status: project.newStatus,
                });
              }
            } else {
              // update current status to newly changed status if it was changed
              var failedInfo = json.failed?.find((p) => p.id === project.id);

              if (failedInfo != null) {
                if (failedInfo.reason === SaveFailReason.StatusChanged) {
                  newProjects.push({
                    ...project,
                    status: failedInfo.newStatus!,
                  });
                } else if (failedInfo.reason === SaveFailReason.NotFound) {
                  // ignore, don't add to new projects since the project has been deleted by another user
                }
              } else {
                newProjects.push(project);
              }
            }
          }

          return {
            ...company2,
            projects: newProjects,
          };
        })
      );
    }

    return json;
  };

  return (
    <Wrapper>
      <h2>Massredigera projekt</h2>
      <div className="buttons">
        <a href={data.urls.back} className="button secondary">
          Tillbaka
        </a>
        <a
          className="button secondary"
          href={
            data.showAllProjects
              ? data.urls.showActiveProjects
              : data.urls.showAllProjects
          }
        >
          {data.showAllProjects
            ? "Visa endast aktiva projekt"
            : "Visa alla projekt"}
        </a>
      </div>
      <div className="companies">
        {formData.map((c) => (
          <Projects
            key={c.id}
            company={c}
            onStatusChanged={onProjectStatusChanged}
            showAll={data.showAllProjects}
            onSaveProjects={(projects) => onSaveProjects(c, projects)}
          />
        ))}
        {!formData.length && <p>Kunde inte hitta några aktiva projekt.</p>}
      </div>
      <a href={data.urls.back} className="button secondary">
        Tillbaka
      </a>
    </Wrapper>
  );
};

export default FinalizeProjectsPage;
